import {Component} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {NGXLogger} from 'ngx-logger';
import {SessionService} from '../../../../shared/service/session.service';
import {AuthorizationComponent} from '../../../authorization/authorization.component';
import {Check} from '../../../check/model/check.model';
import {Datastream} from '../../../datastream/model/datastream.model';
import {Feature} from '../../model/feature.model';
import {Sensor} from '../../model/sensor.model';
import {FeatureService} from '../../service/feature.service';
import {SensorService} from '../../service/sensor.service';
import {SensorDetailStreamUpdateLogsNotificationService} from '../stream/sensor-detail-stream-update-logs-notification-service';
import {SensorCheck} from './enums/sensor-check.enum';
import {DatastreamCheck} from './interface/datastream-check.interface';
import {ChecksService} from './service/checks.service';

@Component({
  selector: 'app-sensor-detail-checks',
  templateUrl: './sensor-detail-checks.component.html',
  styleUrls: ['./sensor-detail-checks.component.scss'],
  standalone: false
})
export class SensorDetailChecksComponent extends AuthorizationComponent {
  public sensor: Sensor;
  public isSaveHidden: boolean;
  public datastreams: Datastream[];
  public datastreamsBackup: Datastream[];
  public editedChecks: Check[];
  public payload: DatastreamCheck[];
  private featureid: string = '';
  private doneEditing: boolean = false;

  constructor(private route: ActivatedRoute,
              sessionService: SessionService,
              private router: Router,
              private sensorService: SensorService,
              private logger: NGXLogger,
              private checksService: ChecksService,
              private streamNotificationService: SensorDetailStreamUpdateLogsNotificationService,
              private featureService: FeatureService) {
    super(sessionService);
    this.setAuthorization('/sensors/checks', ['readAll', 'update']);
    this.sensor = route.snapshot.data['sensor'];
    this.isSaveHidden = true;
    this.datastreams = [];
    this.datastreamsBackup = [];
    this.editedChecks = [];
    this.payload = [];
  }

  public getDatastreamsArray(): any[] {
    let results: any[];

    results = this.getDatastreams(this.streamNotificationService.getFeature());
    this.logger.debug('datastreams: ', results);

    return results;
  }

  public disableChecksInput(): any {
    return !this.rights.get('update');
  }

  public getDatastreams(feature: Feature) {
    if (feature !== undefined && feature.id !== undefined && (this.featureid !== feature.id || this.doneEditing)) {

      const datastreams = [];
      for (const proc of feature.processes) {
        for (const dat of proc.datastreams) {
          datastreams.push(dat);
        }
      }

      this.featureid = feature.id;
      this.datastreams = this.copyDataStream(datastreams);
      this.datastreamsBackup = this.copyDataStream(datastreams);

      this.checksService.setDatastreams(this.datastreams);
      this.checksService.setDatastreamsBackup(this.datastreamsBackup);

      this.isSaveHidden = this.hideSaveButton();
      this.doneEditing = false;
    }
    return this.datastreams;
  }

  copyDataStream(datastreams: any) {
    const datastreamsList: any[] = [];

    datastreams.forEach((datastream) => {
      datastreamsList.push(JSON.parse(JSON.stringify(datastream)));
    });

    return datastreamsList;
  }

  // only show save button if check values have changed.
  hideSaveButton(): boolean {
    let isHidden = true;
    this.datastreams.forEach((datastream: Datastream, index) => {
      if (JSON.stringify(this.datastreamsBackup[index]) !== (JSON.stringify(this.datastreams[index]))) {
        isHidden = false;
      }
    });

    return isHidden;
  }

  filterEditedChecks(datastreamIndex, checkIndex): void {
    // Prevent duplicates in the editedChecks array.
    this.editedChecks = this.editedChecks.filter((obj) => {
      return obj.id !== this.datastreams[datastreamIndex].checks[checkIndex].id;
    });

    // If check is different than in the backup sensor, push to editedChecks.
    if ((JSON.stringify(this.datastreams[datastreamIndex].checks[checkIndex]) !==
      JSON.stringify(this.datastreamsBackup[datastreamIndex].checks[checkIndex]))) {
      this.editedChecks.push(this.datastreams[datastreamIndex].checks[checkIndex]);
    }
  }

  onModelChange(datastreamIndex, checkIndex): void {
    this.isSaveHidden = this.hideSaveButton();
    this.filterEditedChecks(datastreamIndex, checkIndex);
    this.checksService.setChangedChecks(this.editedChecks);
    this.checksService.setDatastreams(this.datastreams);
    this.datastreams = this.checksService.getDatastreams();
  }

  // prepare array of objects containing updated checks (in format requiredField by backend)
  generatePayload(): DatastreamCheck[] {
    const checksToUpdate: DatastreamCheck[] = [];

    this.editedChecks.forEach((check: Check) => {
      if (check.name === SensorCheck.EXTREME) {
        const extremeCheck: DatastreamCheck = {
          id: check.id,
          min: '',
          max: '',
        };

        // set min and max value.
        check.params.forEach((param) => {
          extremeCheck.min = param.name === SensorCheck.MIN ? param.value : extremeCheck.min;
          extremeCheck.max = param.name === SensorCheck.MAX ? param.value : extremeCheck.max;
        });

        checksToUpdate.push(extremeCheck);

      } else if (check.name === SensorCheck.DELTA) {
        const deltaCheck: DatastreamCheck = {
          id: check.id,
          delta: check.params[0].value,
        };

        checksToUpdate.push(deltaCheck);

      } else {
        const genericCheck: DatastreamCheck = {
          id: check.id,
          defaultValue: check.params[0].value,
        };

        checksToUpdate.push(genericCheck);
      }
    });

    return checksToUpdate;
  }

  onSaveClick(): void {
    this.payload = this.generatePayload();
    this.sensorService.updateChecks(this.payload).subscribe(() => {

      // reset editedChecks, hide save button.
      this.editedChecks = [];
      this.isSaveHidden = true;

      // copy datastreams from the updated sensor object, and store them in the checksService.
      this.datastreamsBackup = this.copyDataStream(this.datastreams);
      this.checksService.setSensorId(this.sensor.id);
      this.checksService.setDatastreams(this.datastreams);
      this.checksService.setDatastreamsBackup(this.datastreamsBackup);

      this.featureService.getSensorFeatures(this.sensor.id).subscribe(
        (featureItems) => {
          for (const f of featureItems) {
            if (f.id === this.featureid) {
              this.doneEditing = true;
              this.streamNotificationService.setFeature(f);
              break;
            }
          }
        });

    }, (error) => {
      this.logger.debug('error:', error.value.statusText);
      this.router.navigate(['/error']);
    });
  }
}
